import { Location } from '@angular/common';
import { Component, computed, linkedSignal, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { from, map, mergeMap, Subject, takeUntil, tap } from 'rxjs';
import { DropdownDirective } from 'src/app/directives/dropdown.directive';
import { MediaType } from 'src/app/models/enums/MediaType.enum';
import { UrlParams } from 'src/app/models/enums/Route.enum';
import { ViewStock } from 'src/app/models/enums/ViewStock.enum';
import { Price } from 'src/app/models/types/Product/Price.type';
import { HighJewelryStockInfo, Product, ProductDetailPageDTO, ProductParureDTO, Products, Video, Videos } from 'src/app/models/types/Product/Product.type';
import { BatchStockDTO, CompaniesWithBatchStocks, CompaniesWithStoreStocks, CompanyWithBatchStocks, CompanyWithStoreStocks, HEWBatchStockDTO, SizesWithStock, StockCounters, StockDTO, StocksDTO } from 'src/app/models/types/Product/Stock.type';
import { SharedModule } from 'src/app/modules/shared.module';
import { ProductsService } from 'src/app/services/api/products.service';
import { StockAndPriceService } from 'src/app/services/api/stock-and-price.service';
import { AuthService } from 'src/app/services/auth.service';
import { DynatraceService } from 'src/app/services/dynatrace.service';
import { FiltersService } from 'src/app/services/filters.service';
import { LoaderService } from 'src/app/services/loader.service';
import { SharedService } from 'src/app/services/shared.service';
import { ToastService } from 'src/app/services/toast.service';
import { AttachmentsModalComponent } from '../modals/attachments-modal/attachments-modal.component';
import { FullScreenModalComponent } from '../modals/full-screen-modal/full-screen-modal.component';
import { JewelDetailModalComponent } from '../modals/jewel-detail-modal/jewel-detail-modal.component';
import { DetailComponent } from './detail/detail.component';
import { MediaPreviewComponent } from './media-preview/media-preview.component';
import { MediaSelectorComponent } from './media-selector/media-selector.component';
import { PdpService } from './pdp.service';
import { SizeSelectorComponent } from './size-selector/size-selector.component';
import { StockComponent } from './stock/stock.component';

@Component({
  selector: 'app-product-page',
  imports: [
    SharedModule,
    MediaSelectorComponent,
    MediaPreviewComponent,
    DetailComponent,
    FullScreenModalComponent,
    StockComponent,
    SizeSelectorComponent,
    DropdownDirective,
    AttachmentsModalComponent,
    JewelDetailModalComponent,
  ],
  templateUrl: './product-page.component.html',
  styleUrl: './product-page.component.scss'
})
export class ProductPageComponent implements OnInit, OnDestroy {

  @ViewChild('fullScreenModal', { static: false }) fullScreenModal!: FullScreenModalComponent;
  @ViewChild('attachmentModal', { static: false }) attachmentModal!: AttachmentsModalComponent;
  @ViewChild('jewelDetailModal', { static: false }) jewelDetailModal!: JewelDetailModalComponent;

  private readonly _destroying$ = new Subject<void>();

  public readonly SAFFRON_STAR = "./../../../assets/icons/star-saffron.svg";

  public hideViewStockOptions = false;

  public readonly viewStockOptions: ViewStock[] = [ViewStock.ViewMyStore, ViewStock.ViewOtherStore];
  public selectedViewStock: WritableSignal<ViewStock> = signal(ViewStock.ViewMyStore);

  private _pdp: WritableSignal<ProductDetailPageDTO | null> = signal(null);
  public pdp: Signal<ProductDetailPageDTO | null> = computed(() => this._pdp());

  private _price: WritableSignal<Price | null | undefined> = signal(undefined);
  public price: Signal<Price | null | undefined> = computed(() => this._price());

  private _sizesWithStock: WritableSignal<SizesWithStock> = signal([]);
  public sizesWithStock: Signal<SizesWithStock> = computed(() => this._sizesWithStock());

  public selectedMediaType: WritableSignal<MediaType> = signal(MediaType.Image);

  public images: Signal<string[]> = computed(() => this.pdp()?.media || []);

  public media: WritableSignal<string[]> = linkedSignal(() => this.images());
  public selectedMedia: WritableSignal<string> = linkedSignal(() => this.media()[0] || this.SAFFRON_STAR);

  private _highJewelryStockInfo: WritableSignal<HighJewelryStockInfo | null> = signal(null);
  public highJewelryStockInfo: Signal<HighJewelryStockInfo | null> = computed(() => this._highJewelryStockInfo());

  private _attachments: WritableSignal<string[]> = linkedSignal(() => this.media().filter(media => !(media.endsWith('.jpg') || media.endsWith('.png') || media.endsWith('.jpeg'))));
  public attachments: Signal<string[]> = this._attachments.asReadonly();

  private _stoneMedia: WritableSignal<string[]> = linkedSignal(() => this.pdp()?.stoneMedia || []);
  public stoneMedia: Signal<string[]> = this._stoneMedia.asReadonly();

  private _parure: WritableSignal<ProductParureDTO> = signal([]);
  public parureParsedInProduct: Signal<Products> = computed(() => {
    return this._parure().map(parureItem => {
      let product = {
        sapCode: parureItem.sapCode || "",
        catalogCode: parureItem.catCode || "",
        media: parureItem.media || "",
        reproducibility: parureItem.hjReproducibility
      } as Product;
      return product;
    });
  });

  public videos: Signal<Videos> = computed(() => {
    let videos: Videos = [];
    if (this.pdp()) {
      //Occorre filtrare i video per category. Quelli AMV vanno come link nel pulsante in alto a destra
      this.pdp()?.videos.forEach(categoryVideo => {
        if (categoryVideo.category.toLowerCase() !== "amv") {
          videos = videos.concat(categoryVideo.videos)
        }
      });
    }
    return videos;
  });

  public amvVideos: Signal<Videos> = computed(() => {
    if (this.pdp()) {
      let amvVideos: Videos = [];
      if (this.pdp()!.videos) {
        this.pdp()!.videos.forEach(categoryVideo => {
          if (categoryVideo.category.toLowerCase() === "amv") {
            amvVideos = amvVideos.concat(categoryVideo.videos);
          }
        });
      }
      return amvVideos;
    }
    return []
  });

  public accessoriesMedia: Signal<string[]> = computed(() => {
    if (this.pdp()) {
      return this.pdp()!.accessoriesMedia;
    }
    return [];
  });

  public digitalPassportUrl: Signal<string> = computed(() => {
    let details = this.pdp()?.details || [];
    let digitalPassportDetail = details.find(detail => detail.fieldName.toLowerCase() === "digitalpassport");
    if (digitalPassportDetail) {
      return digitalPassportDetail.value as string;
    }
    return "";
  });

  public selectedVideo: WritableSignal<Video | undefined> = linkedSignal(() => {
    let videoCover = this.selectedMedia();
    let video = this.videos().find(v => v.cover === videoCover);
    return video;
  });

  public model3DUrl: WritableSignal<string> = signal("");

  private _viewStock: WritableSignal<boolean> = signal(false);
  public viewStock: Signal<boolean> = computed(() => this._viewStock());

  public showStockLoader: WritableSignal<boolean> = signal(false);

  private _stock: WritableSignal<StocksDTO | null> = signal(null);
  public stock: Signal<StocksDTO | null> = computed(() => this._stock());

  private _batchStock: WritableSignal<BatchStockDTO | null> = signal(null);
  public batchStock: Signal<BatchStockDTO | null> = computed(() => this._batchStock());

  private _hewStock: WritableSignal<HEWBatchStockDTO | null> = signal(null);
  public hewStock: Signal<HEWBatchStockDTO | null> = computed(() => this._hewStock());

  private _hqStock: WritableSignal<CompaniesWithStoreStocks | null> = signal(null);
  public hqStock: Signal<CompaniesWithStoreStocks | null> = computed(() => this._hqStock());

  private _hqBatchStock: WritableSignal<CompaniesWithBatchStocks | null> = signal(null);
  public hqBatchStock: Signal<CompaniesWithBatchStocks | null> = computed(() => this._hqBatchStock());

  public downloadingPdf: WritableSignal<boolean> = signal(false);

  public get isBatchManaged(): boolean {
    return !!this.pdp()?.isBatchManaged;
  }

  public get canShowImageButton(): boolean {
    return this.images().length > 0 && !this.viewStock();
  }

  public get canShowVideoButton(): boolean {
    return this.videos().length > 0 && !this.viewStock();
  }

  public get canShowModel3DButton(): boolean {
    return !!this.model3DUrl() && !this.viewStock();
  }

  public get isHighJewelry(): boolean {
    return this._pdp()?.category === "HIGH_JEWELRY";
  }

  public get isHighEndWatches(): boolean {
    return this._pdp()?.category === "HAUTE_HOROLOGERIE";
  }

  public get hideStockViewMenu(): boolean {
    if (this.viewStock()) {
      return (this.isBatchManaged || this.isHighEndWatches || this.isHighJewelry);
    }
    return true;
  }

  public get dwUrl(): string {
    let baseUrl = environment.dwUrl;
    let assetId = this.pdp()?.assetId;;
    return `${baseUrl}${assetId}`;
  }

  constructor(
    public readonly authService: AuthService,
    public readonly loaderService: LoaderService,
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly pdpService: PdpService,
    private readonly toastService: ToastService,
    private readonly sharedService: SharedService,
    private readonly productsService: ProductsService,
    private readonly dynatraceService: DynatraceService,
    private readonly stockAndPriceService: StockAndPriceService,
  ) { }

  ngOnInit(): void {
    this.hideViewStockOptions = FiltersService.STORE_PEZZOTTI_COME_PORTO_RICO.includes(this.authService.store?.codeStore || '');

    this.route.paramMap.pipe(takeUntil(this._destroying$))
      .subscribe((params) => {
        let sapCode = params.get(UrlParams.SapCode);
        if (sapCode) {
          this.loadPDP(sapCode);
        }
      });

    this.pdpService.updateSizeCounter$.pipe(takeUntil(this._destroying$))
      .subscribe((saps: string[] | undefined) => {
        this._sizesWithStock.update(sizes => sizes.map(size => ({ ...size, counter: 0 })));
        let sapCodes = saps;
        if (!sapCodes) {
          sapCodes = [];
          // Estrae tutti i sapCode presenti negli storeStocks di tutte le companies, escludendo i duplicati
          const sapCodesSet = new Set<string>();
          const hqStock = this.hqStock();
          if (hqStock) {
            hqStock.forEach(company => {
              company.storeStocks?.forEach(storeStock => {
                if (storeStock.sapcode) {
                  sapCodesSet.add(storeStock.sapcode);
                }
              });
            });
            sapCodes = Array.from(sapCodesSet);
          }
        }
        this._sizesWithStock.update((sizes) => {
          sizes.forEach(size => {
            if (sapCodes?.includes(size.sapCode)) {
              size.counter += 1;
            }
          });
          return sizes;
        });
      });
  }

  ngOnDestroy(): void {
    this.pdpService.reset();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  loadPDP(sapCode: string) {
    /**
     * Nei casi in cui cambio size o colore e devo ri-aggiornare la pdp coaricando il nuovo sapcode,
     * devo fare in modo che l'utente, se refresha la pagina si ritrova nella pdp dell'ultimo prodotto selezionato.
     * Quindi sostituisco il sapcode presente nell'URL con quello passato come parametro.
     */
    const url = this.location.path().replace(/\/[^\/]+$/, `/${sapCode}`);
    this.location.replaceState(url);

    this.loaderService.show();
    this.productsService.getPDP(sapCode).subscribe({
      next: (pdp) => {
        try {
          this._pdp.set(pdp);
          this.pdpService.setPdp(pdp);
          this.dynatraceService.logProduct(pdp.sapCode, pdp.aestheticLine || "");
          //Setto le size inizialmente con counter a 0
          this._sizesWithStock.set(pdp.sizes?.map(size => ({ ...size, counter: 0 })) || []);

          if (pdp && !this.viewStock()) {
            this.loadModel3DUrl(pdp.model3DUrl);
          }

          this.loadPrice(pdp.sapCode);
          this.loadStockInBackgroud();
          this.loadParure(pdp.sapCode);
          //Se sto caricando la pdp ma sono già visualizzando lo stock allora facio in modo di
          //ricaricare lo stock anche quello che viene caricato al click di "view stock" e non solo quello in background
          if (this.viewStock()) {
            this.switchToStockView();
          }
        } catch (err) {
          console.log(err);
        } finally {
          this.loaderService.hide();
        }
      },
      error: (err) => {
        this.toastService.showError("Unable to load PDP");
        this.loaderService.hide();
      }
    })
  }

  loadPrice(sapCode: string) {
    this.stockAndPriceService.getPriceBySapCode(sapCode).subscribe({
      next: (res) => {
        this._price.set(res);
      },
      error: (err) => {
        this._price.set(null);
        console.log(err);
        this.toastService.showError("Unable to load Price");
      }
    })
  }

  loadParure(sapCode: string) {
    this.productsService.getParure(sapCode).subscribe({
      next: (res) => {
        this._parure.set(res);
      },
      error: (err) => {
        this._parure.set([]);
        console.log(err);
        this.toastService.showError("Unable to load Parure");
      }
    })
  }

  loadModel3DUrl(model3DUrl: string) {
    return;
    //! Rimozione temporanea del caricamento del modello 3D
    // if (!model3DUrl) return;
    // this.loaderService.show();
    // this.productsService.getModel3DUrl(model3DUrl).subscribe({
    //   next: (res) => {
    //     console.log(res);
    //     this.model3DUrl.set(res.signed_url);
    //     this.loaderService.hide();
    //   },
    //   error: (err) => {
    //     this.toastService.showError("Unable to load 3D Model URL");
    //     this.loaderService.hide();
    //   }
    // })
  }

  handleGoBack() {
    if (this.viewStock()) {
      this._viewStock.set(false);
      this.selectedViewStock.set(ViewStock.ViewMyStore);
    } else {
      this.location.back();
    }
  }

  handleSelectMedia(mediaType: MediaType) {
    switch (mediaType) {
      case MediaType.Image: {
        this.media.set(this.pdp()?.media || []);
        break;
      }
      case MediaType.Video: {
        this.media.set(this.videos().map(v => v.cover) || []);
        break;
      }
      case MediaType.Model3D: {
        this.media.set([]);
        if (this.canShowModel3DButton) {
          this.selectedMedia.set(this.model3DUrl() as string);
        }
        break;
      }
    }
    this.selectedMediaType.set(mediaType);
  }

  loadStockInBackgroud() {
    //Gli utenti NoStore e BP non possono visualizzare lo stock
    if (this.authService.user?.isUserNoStore || this.authService.user?.isUserBp) {
      return;
    }
    switch (true) {
      case this.isHighJewelry:
        this.loadHighJewelryStockInfo();
        break;
      case this.isHighEndWatches:
        this.loadHighEndWatchesStock();
        break;
      case this.isBatchManaged:
        //Se NON sono un utente HQ posso caricare lo stock a batch
        if (!this.authService.user?.isUserGenerallyHQ) {
          this.loadStockBatch();
        }
        break;
      default:
        //Se NON sono un utente HQ posso caricare lo stock
        if (!this.authService.user?.isUserGenerallyHQ) {
          this.loadStock();
        }
        break;
    }
  }

  loadStockForHqUser() {
    //Se il prodotto è HJ o HEW non carico lo stock perché è stato già caricato in background
    if (!this.isHighJewelry && !this.isHighEndWatches) {
      if (this._hqBatchStock() !== null || this._hqStock() !== null) {
        if (this.isBatchManaged) {

        } else {
          this._hqStock.update(companies => {
            if (companies) {
              companies.forEach(company => {
                company.stockCounters = this.getStocksCounters(company.storeStocks || [], this.pdp()?.sapCode);
              })
            }
            return companies;
          });
        }

        return;
      }
      switch (true) {
        case this.isBatchManaged: {
          //Resetto lo stock già carcato per avere una soluzione pulita
          // this._hqBatchStock.set(null);
          this.loadStockBatch_HQ();
          break;
        }
        default: {
          //Resetto lo stock già carcato per avere una soluzione pulita
          // this._hqStock.set(null);
          this.loadStock_HQ();
          break;
        }
      }
    }
  }

  loadStockForGlobalHqUser() {
    //Se il prodotto è HJ o HEW non carico lo stock perché è stato già caricato in background
    if (!this.isHighJewelry && !this.isHighEndWatches) {
      if (this.pdpService.initializedGlobalAreaStock || this.pdpService.initializedGlobalAreaBatchStock) {
        if (this.isBatchManaged) {
          this.pdpService.updateGlobalAreaBatchStock_sapCodeChanged();
        } else {
          this.pdpService.updateGlobalAreaStock_sapCodeChanged();
        }
        return;
      }
      this.showStockLoader.set(true);
      //Carico tutte le aree e per ogni company di ogni area effetto la rispettiva chiamata per lo stock
      this.stockAndPriceService.getGlobalAreas().subscribe({
        next: (res) => {
          if (this.isBatchManaged) {
            this.pdpService.initGlobalAreaBatchStock(res);
          } else {
            this.pdpService.initGlobalAreaStock(res);
          }
          this.showStockLoader.set(false);
        },
        error: (err) => {
          console.log(err);
          this.showStockLoader.set(false);
        }
      });
    }
  }

  switchToStockView() {
    //Gli utenti NoStore e BP non possono visualizzare lo stock
    if (this.authService.user?.isUserNoStore || this.authService.user?.isUserBp) {
      return;
    }
    this._viewStock.set(true);
    this.resetMediaVisualization();
    if (this.authService.user?.isUserHQ) {
      this.loadStockForHqUser();
    }
    if (this.authService.user?.isUserGlobalHQ) {
      this.loadStockForGlobalHqUser();
    }
  }

  loadStock() {
    //TODO: Gestire anche qui le size
    this.showStockLoader.set(true);
    this.stockAndPriceService.getProductStock(this.pdp()!.sapCode, this.pdp()!.catalogCode).subscribe({
      next: (res) => {
        this._stock.set(res);
        this.getStocksCounters(res);
        this.showStockLoader.set(false);
      },
      error: (err) => {
        console.log(err);
        this.showStockLoader.set(false);
        //! Rimosso per Albanese
        // this.toastService.showError("Unable to load Stock");
      }
    })
  }

  loadStock_HQ() {
    this.showStockLoader.set(true);
    const sapCode = this.pdp()!.sapCode;
    const catalogCode = this.pdp()!.catalogCode;
    let companiesWithStoreStocks: CompaniesWithStoreStocks = [];
    this._hqStock.set(new Array(this.authService.companyCodes?.length || 0).fill(null));
    //Costruisco l'array di API da invocare per il caricamento dello stock HQ per ogni company
    from(this.authService.companyCodes).pipe(
      mergeMap((company, index) => {
        let temp: CompanyWithStoreStocks = {
          ...company,
          //Setto ad undefined per mostrare il loader prima che i dati vengano caricati
          //Se non ho store al di sotto allora setto ad array vuoto e il loader non viene mostrato più
          storeStocks: undefined,
          //Setto i contatori di tutti gli store della company a 0 prima di
          stockCounters: {
            free: 0,
            reservation: 0,
            consignmentIn: 0,
            inTransit: 0
          }
        };
        companiesWithStoreStocks.push(temp);
        this._hqStock.update(companies => {
          if (companies) {
            companies[index] = temp;
          }
          return companies
        });
        return this.stockAndPriceService.getProductStock_HQ(sapCode, catalogCode, company.code).pipe(
          //Aggancio l'index alla response per poter aggiornare i loader
          map(res => ({ res, index }))
        );
      })
    ).subscribe({
      next: (response) => {
        //Man mano che si caricano gli stock aggiorno le companies
        let { res, index } = response;
        this._hqStock.update(companies => {
          if (companies) {
            companies[index].storeStocks = res;
            companies[index].stockCounters = this.getStocksCounters(res, sapCode);
            // companies[index].stockCounters = this.getStockCounters(res.find(s => s.sapcode === this.pdp()?.sapCode));
          }
          return companies;
        });
        this._hqStock.set([...this.hqStock()!]);
      },
      complete: () => {
        this.showStockLoader.set(false);
      },
      error: (err) => {
        console.log(err);
        this.showStockLoader.set(false);
        //! Rimosso per Albanese
        // this.toastService.showError("Unable to load Stock");
      }
    })
  }

  loadStockBatch_HQ() {
    this.showStockLoader.set(true);
    const catalogCode = this.pdp()!.catalogCode;
    let companiesWithBatchStocks: CompaniesWithBatchStocks = [];
    this._hqBatchStock.set(new Array(this.authService.companyCodes?.length || 0).fill(null));
    //Costruisco l'array di API da invocare per il caricamento dello stock HQ per ogni company
    from(this.authService.companyCodes).pipe(
      mergeMap((company, index) => {
        let temp: CompanyWithBatchStocks = {
          ...company,
          //Setto ad undefined per mostrare il loader prima che i dati vengano caricati
          batchStocks: undefined,
          //Setto i contatori di tutti gli store della company a 0 prima di
          stockCounters: {
            free: 0,
            reservation: 0,
            consignmentIn: 0,
            inTransit: 0
          }
        }
        companiesWithBatchStocks.push(temp);
        this._hqBatchStock.update(companies => {
          if (companies) {
            companies[index] = temp;
          }
          return companies
        });
        return this.stockAndPriceService.getBatchProductStock_HQ(catalogCode, company.code).pipe(
          //Aggancio l'index alla response per poter aggiornare i loader
          map(res => ({ res, index }))
        );
      }),
      tap(() => {
        //Setto inizialmente l'hqBatchStock con tutte le companies ma con gli stock vuoti
      })
    ).subscribe({
      next: (response) => {
        //Man mano che si caricano gli stock aggiorno le companies
        let { res, index } = response;
        this._hqBatchStock.update(companies => {
          if (companies && companies[index]) {
            companies[index].batchStocks = res;
            companies[index].stockCounters = this.getHqBatchStocksCounters(res);
          }
          return companies;
        });
        this._hqBatchStock.set([...this._hqBatchStock()!]);
      },
      complete: () => {
        this.showStockLoader.set(false);
      },
      error: (err) => {
        console.log(err);
        this.showStockLoader.set(false);
        //! Rimosso per Albanese
        // this.toastService.showError("Unable to load Stock");
      }
    })
  }

  getHqBatchStocksCounters(batchStock: BatchStockDTO): StockCounters {
    let free = 0, reservation = 0, consignmentIn = 0, inTransit = 0;
    batchStock.forEach(stock => {
      const counters = this.stockAndPriceService.getStockCounters(stock.storeStocks);
      free += counters.free;
      reservation += counters.reservation;
      consignmentIn += counters.consignmentIn;
      inTransit += counters.inTransit;
    })
    return { free, reservation, consignmentIn, inTransit };
  }

  getStocksCounters(stocks: StocksDTO, sapCode?: string): StockCounters {
    let free = 0, reservation = 0, consignmentIn = 0, inTransit = 0;
    stocks.forEach(stock => {
      const counters = this.getStockCounters(stock);
      free += counters.free;
      reservation += counters.reservation;
      consignmentIn += counters.consignmentIn;
      inTransit += counters.inTransit;
    });
    if (sapCode) {
      free = 0;
      reservation = 0;
      consignmentIn = 0;
      inTransit = 0;
      let stock = stocks.find(s => s.sapcode === sapCode);
      if (stock) {
        stock.storeStocks?.forEach(storeStock => {
          const counters = this.stockAndPriceService.getStockCounters(storeStock);
          free += counters.free;
          reservation += counters.reservation;
          consignmentIn += counters.consignmentIn;
          inTransit += counters.inTransit;
        })
      }
      return { free, reservation, consignmentIn, inTransit };
    }
    return { free, reservation, consignmentIn, inTransit };
  }

  getStockCounters(stock?: StockDTO): StockCounters {
    let free = 0, reservation = 0, consignmentIn = 0, inTransit = 0;
    if (stock) {
      let storeStocks = [...stock.storeStocks || []];
      if (this.authService.user?.isUserStore && this.selectedViewStock() === ViewStock.ViewMyStore) {
        storeStocks = storeStocks.filter(store => store.myStore);
      }
      storeStocks?.forEach(storeStock => {
        const counters = this.stockAndPriceService.getStockCounters(storeStock);
        free += counters.free;
        reservation += counters.reservation;
        consignmentIn += counters.consignmentIn;
        inTransit += counters.inTransit;
      })
    }
    //Aggiorno anche il contatore della size corrispondente
    let i = this.sizesWithStock().findIndex(size => size.sapCode === stock?.sapcode);
    if (i !== -1) {
      this._sizesWithStock.update(sizes => {
        let size = sizes[i];
        size.counter += free;
        return sizes;
      });
    }
    return { free, reservation, consignmentIn, inTransit }
  }

  loadStockBatch() {
    this.showStockLoader.set(true);
    this.stockAndPriceService.getBatchProductStock(this.pdp()!.catalogCode).subscribe({
      next: (res) => {
        this._batchStock.set(res);
        this.showStockLoader.set(false);
      },
      error: (err) => {
        console.log(err);
        this.showStockLoader.set(false);
        //! Rimosso su richiesta di Albanese
        // this.toastService.showError("Unable to load Stock");
      }
    })
  }

  loadHighEndWatchesStock() {
    this.showStockLoader.set(true);
    this.stockAndPriceService.getHighEndWatchesStock(this.pdp()!.sapCode).subscribe({
      next: (res) => {
        this._hewStock.set(res);
        this.showStockLoader.set(false);
      },
      error: (err) => {
        console.log(err);
        this.showStockLoader.set(false);
        //! Rimosso su richiesta di Albanese
        // this.toastService.showError("Unable to load Stock");
      }
    })
  }

  loadHighJewelryStockInfo() {
    this.showStockLoader.set(true);
    this.stockAndPriceService.getHighJewelryStockInfo(this.pdp()!.sapCode).subscribe({
      next: (res) => {
        this._highJewelryStockInfo.set(res);
        this.showStockLoader.set(false);
      },
      error: (err) => {
        console.log(err);
        this.showStockLoader.set(false);
        this.toastService.showError("Unable to load High Jewelry Stock Information");
      }
    })
  }

  resetMediaVisualization() {
    this.handleSelectMedia(MediaType.Image);
    this.selectedMedia.set(this.media()[0] || this.SAFFRON_STAR);
  }

  handleStockViewChange(value: ViewStock) {
    this.selectedViewStock.set(value);
    if (this.stock()) {
      this._sizesWithStock.update(sizes => sizes.map(size => ({ ...size, counter: 0 })));
      this.getStocksCounters(this.stock()!);
    }
  }

  handleOpenFullScreenModal(flag: boolean) {
    this.fullScreenModal.openModal(this.media(), this.selectedMedia());
  }

  handleOpenAttachmentsModal(stoneMedia?: boolean) {
    this.attachmentModal.openModal(stoneMedia ? this.stoneMedia() : this.attachments());
  }

  handleOpenJewelDetailModal() {
    this.jewelDetailModal.openModal(this.pdp()?.labMedias || [], this.pdp()?.labMediaImages || []);
  }

  downloadStoneMedia() {
    if (this.stoneMedia() && this.stoneMedia()[0]) {
      // this.productsService.downloadFromUrl(this.stoneMedia()[0], '');
      this.sharedService.navigateTo_PdfPage(this.stoneMedia()[0]);
    }
  }

  getProductCard(withPriceList?: boolean) {
    this.downloadingPdf.set(true);
    this.productsService.getPdf(this.pdp()!.sapCode, withPriceList).subscribe({
      next: (res) => {
        if (res.success) {
          let fileName = `ProductCard_${this.pdp()!.sapCode}.pdf`;
          if (withPriceList) {
            fileName = `ProductCard_PriceList_${this.pdp()!.sapCode}.pdf`;
          }
          this.productsService.downloadFromUrl(res.url, fileName);
        } else {
          this.toastService.showError(res.error);
        }
        this.downloadingPdf.set(false);
      },
      error: (err) => {
        this.downloadingPdf.set(false);
        console.log(err);
        this.toastService.showError("Unable to download the PDF");
      }
    })
  }

}
